import { ReactNode } from "react";
import { ResourceType } from "@app/resources/resource";

export interface GapCountResult {
  totalGapCount: number;
}

/**
 * Per-membership Gap by County Status counts. Null fields mean "no data" (not yet loaded, failed, or the
 * status isn't supported for this project type) - zero is reserved for actual data that happens to be zero.
 */
export interface GapByCountyStatusBreakdown {
  active: number | null;
  pending: number | null;
  approved: number | null;
  opportunity: number | null;
}

/**
 * Fetched independently of {@link NetworkPortfolioManagementProject} (progressive load), keyed
 * by projectId so the frontend can join it back to the card it belongs to. `primary`/`secondary` mirror the
 * project's own dual-membership toggle. `hasError` means the lookup failed for this project - render an
 * inline error, not zeroed cells.
 */
export interface GapByCountyStatusResult {
  projectId: number;
  primary: GapByCountyStatusBreakdown | null;
  secondary: GapByCountyStatusBreakdown | null;
  hasError: boolean;
}

/**
 * A filtered project resource paired with its resource type, returned by the project-resources endpoint.
 * The type lets bulk Run/Publish route each project to the zip vs. custom endpoint without a second call —
 * including Select All across pages that were never loaded.
 */
export interface ProjectResource {
  resourceId: number;
  resourceType: ResourceType;
}

export type ViewMode = "cards" | "rows";

export enum NetworkPortfolioManagementProjectTypeEnum {
  Unknown = "unknown",
  CountyNetworkAssessment = "countyNetworkAssessment",
  ZipCodeNetworkAssessment = "zipCodeNetworkAssessment",
  Accuracy = "accuracy",
  Blended = "blended",
}

export enum NetworkPortfolioManagementProjectStatus {
  Running = "running",
  PublishPending = "publishPending",
  Canceled = "canceled",
  Failed = "failed",
  NeedsAttention = "needsAttention",
  Complete = "complete",
  NotRun = "notRun",
}

export const networkPortfolioManagementProjectStatusDisplayLabel: Record<NetworkPortfolioManagementProjectStatus, string> = {
  // The backend enum is "Running"; the dashboard displays it as "Processing"
  [NetworkPortfolioManagementProjectStatus.Running]: "Processing",
  [NetworkPortfolioManagementProjectStatus.PublishPending]: "Publish Pending",
  [NetworkPortfolioManagementProjectStatus.Canceled]: "Canceled",
  [NetworkPortfolioManagementProjectStatus.Failed]: "Failed",
  [NetworkPortfolioManagementProjectStatus.NeedsAttention]: "Needs Attention",
  [NetworkPortfolioManagementProjectStatus.Complete]: "Complete",
  [NetworkPortfolioManagementProjectStatus.NotRun]: "Not Run",
};

/** Project Status filter options in the dashboard's priority order (Publish Pending and Not Run are Preview-only). */
export const projectStatusFilterOrder: readonly NetworkPortfolioManagementProjectStatus[] = [
  NetworkPortfolioManagementProjectStatus.Canceled,
  NetworkPortfolioManagementProjectStatus.Failed,
  NetworkPortfolioManagementProjectStatus.Running,
  NetworkPortfolioManagementProjectStatus.PublishPending,
  NetworkPortfolioManagementProjectStatus.NeedsAttention,
  NetworkPortfolioManagementProjectStatus.Complete,
  NetworkPortfolioManagementProjectStatus.NotRun,
];

/** Statuses that require the Preview role; offered only to Preview users and stripped for everyone else. */
export const previewGatedProjectStatuses: readonly NetworkPortfolioManagementProjectStatus[] = [
  NetworkPortfolioManagementProjectStatus.PublishPending,
  NetworkPortfolioManagementProjectStatus.NotRun,
];

/**
 * Server-computed eligibility for bulk admin actions on a project. Mirrors the .NET
 * NetworkPortfolioManagementProjectActionStatusEnum and is consumed directly — the UI does no
 * client-side eligibility logic.
 */
export enum NetworkPortfolioManagementProjectActionStatus {
  Run = "run",
  Publish = "publish",
  Archive = "archive",
}

/** A selected Project Action filter, or null for "no filter" (matching the nullable backend enum). */
export type ProjectActionFilter = NetworkPortfolioManagementProjectActionStatus | null;

export const projectActionDisplayLabel: Record<NetworkPortfolioManagementProjectActionStatus, string> = {
  [NetworkPortfolioManagementProjectActionStatus.Archive]: "Archive Projects",
  [NetworkPortfolioManagementProjectActionStatus.Publish]: "Ready to Publish",
  [NetworkPortfolioManagementProjectActionStatus.Run]: "Ready to Run",
};

/**
 * Network Portfolio Management Project Dashboard's Resource views.
 * Resource View is about the Resource Status (Active,Hidden,Archived) more than information about the project.
 */
export enum NetworkPortfolioManagementResourceView {
  Active = "active",
  Archived = "archived",
  Hidden = "hidden",
}

export const networkPortfolioManagementProjectTypeDisplayLabel: Partial<Record<NetworkPortfolioManagementProjectTypeEnum, string>> = {
  [NetworkPortfolioManagementProjectTypeEnum.CountyNetworkAssessment]: "County Level Network Adequacy",
  [NetworkPortfolioManagementProjectTypeEnum.ZipCodeNetworkAssessment]: "Zip Code Level Network Adequacy",
  [NetworkPortfolioManagementProjectTypeEnum.Accuracy]: "Accuracy",
  [NetworkPortfolioManagementProjectTypeEnum.Blended]: "Blended",
};

/** The project types a user can select in the Project Type filter, in the order the dashboard renders them. */
export const selectableProjectTypes: readonly NetworkPortfolioManagementProjectTypeEnum[] = [
  NetworkPortfolioManagementProjectTypeEnum.CountyNetworkAssessment,
  NetworkPortfolioManagementProjectTypeEnum.ZipCodeNetworkAssessment,
  NetworkPortfolioManagementProjectTypeEnum.Accuracy,
];

export interface NetworkPortfolioManagementMembership {
  memberSetAlias: string | null;
  countyCount: number | null;
  zipCodeCount: number | null;
  adequacyScore: number | null;
  accuracyScore: number | null;
  gapCount: number | null;
  adequacyMetCount: number | null;
  adequacyMetPercent: number | null;
  adequacyTotalGapCount: number | null;
  adequacyTotalGapPercent: number | null;
  adequacyApprovedExceptionsCount: number | null;
  accuracyIndexScore: number | null;
  accuracyAttestationRate: number | null;
  accuracyAccuratePercent: number | null;
  accuracyInaccuratePercent: number | null;
  accuracySuspectPercent: number | null;
  accuracyEnrichedPercent: number | null;
  accuracySkippedPercent: number | null;
}

export interface NetworkPortfolioManagementProject {
  projectId: number;
  name: string;
  lastModifiedDate: string | null;
  projectType: NetworkPortfolioManagementProjectTypeEnum;
  isDualMembership: boolean;
  primary: NetworkPortfolioManagementMembership | null;
  secondary: NetworkPortfolioManagementMembership | null;
  status: NetworkPortfolioManagementProjectStatus | null;
  /** ISO date the project was archived; null for active projects. Drives the archived-view date pill. */
  dateArchived: string | null;
  lineOfBusiness: string | null;
  templateName: string | null;
  hasUnprocessedProviderTerminations: boolean;
  hasUnprocessedProjectionProviders: boolean;
  hasUnprocessedSpecialtyGroupExceptions: boolean;
  tags: Record<string, string[]>;
  /**
   * Whether the project is shared to the current user (vs. owned by them). This is flatten from
   * the Resource's IsShared.
   */
  isShared: boolean;
  /**
   * Whether the current user has hidden this shared project. Flattened from the Resource's
   * ResourceSharing.IsHidden. Drives the Hidden view's Unhide label.
   */
  isHidden: boolean;
}

export interface FilterOption {
  value: string;
  displayName: string;
  /** Present only for nested dropdowns (e.g. counties under a state). */
  children?: FilterOption[];
}

export interface FilterOptions {
  linesOfBusiness: FilterOption[];
  projectNames: FilterOption[];
  projectUtilizations: FilterOption[];
  serviceAreas: FilterOption[];
  specialties: FilterOption[];
  tags: FilterOption[];
  templates: FilterOption[];
}

export interface CountyRef {
  state: string;
  county: string;
}

/**
 * A specialty group selection. A specialty group code is not unique across templates (the same code can carry
 * different names), so a group is identified by code + name together.
 */
export interface SpecialtyGroupRef {
  readonly code: string;
  readonly name: string;
}

export interface ActiveFilters {
  /**
   * Network-assessment project-type filter (client-facing; shown to all roles). County Network Adequacy and
   * Accuracy each also match Blended projects; Blended is never independently selectable. Empty ⇒ no filtering.
   * Saved/restored with the view.
   */
  readonly projectTypes: readonly NetworkPortfolioManagementProjectTypeEnum[];
  readonly linesOfBusinessIds: readonly number[];
  readonly serviceAreaStates: readonly string[];
  readonly serviceAreaCounties: readonly CountyRef[];
  readonly specialtyGroups: readonly SpecialtyGroupRef[];
  readonly tagIds: readonly number[];
  /** Template-name filter; the filter value is the resolved TemplateName itself (no numeric id). */
  readonly templateNames: readonly string[];
  /**
   * Project run-status filter (client-facing; shown to all roles). Publish Pending and Not Run are Preview-gated and
   * offered only to Preview users; the other five values are available to everyone. Empty ⇒ no filtering.
   * Saved/restored with the view.
   */
  readonly projectStatuses: readonly NetworkPortfolioManagementProjectStatus[];
  readonly searchTerm: string;
  /** Below are Role Restricted Filters */
  readonly projectAction: ProjectActionFilter;
  readonly projectUtilizationCategoryIds: readonly number[];
}

export interface SavedView {
  readonly npmFilteredViewId: number;
  readonly name: string;
  readonly description: string | null;
  readonly filters: ActiveFilters;
  readonly isDefault: boolean;
  readonly isQesView: boolean;
  /**
   * True when some of the view's saved filters could not be loaded (a filter's shape changed since it was saved).
   * The affected filters are dropped; the user must re-save the view. failedFilterProperties names them.
   * Optional so existing SavedView fixtures/literals stay valid; toSavedView always populates it (default false).
   */
  readonly hasFilterLoadError?: boolean;
  readonly failedFilterProperties?: readonly string[];
}

export interface NpmFilteredViewResponse {
  readonly npmFilteredViewId: number;
  readonly name: string;
  readonly description: string | null;
  readonly isDefault: boolean;
  readonly hasFilterLoadError?: boolean;
  readonly failedFilterProperties?: readonly string[];
  readonly filters: {
    readonly linesOfBusinessIds: readonly number[];
    readonly projectTypes?: readonly NetworkPortfolioManagementProjectTypeEnum[];
    readonly searchTerm: string;
    readonly serviceAreaStates: readonly string[];
    readonly serviceAreaCounties: readonly { readonly state: string; readonly county: string }[];
    readonly specialtyGroups: readonly SpecialtyGroupRef[];
    readonly tagIds: readonly number[];
    readonly templateNames: readonly string[];
    readonly projectAction?: ProjectActionFilter;
    readonly projectStatuses?: readonly NetworkPortfolioManagementProjectStatus[];
    readonly projectUtilizationCategoryIds?: readonly number[];
  };
}

export interface SavedViewRequest {
    readonly description: string;
    readonly filters: ActiveFilters;
    readonly isDefault: boolean;
    readonly name: string;
}

export enum SortByOption {
  AdequacyScoreAscending = "adequacyScoreAscending",
  AdequacyScoreDescending = "adequacyScoreDescending",
  AlphabeticalAZ = "alphabeticalAZ",
  AlphabeticalZA = "alphabeticalZA",
  ArchiveDateAscending = "archiveDateAscending",
  ArchiveDateDescending = "archiveDateDescending",
  CountyCountAscending = "countyCountAscending",
  CountyCountDescending = "countyCountDescending",
  LastUpdatedAscending = "lastUpdatedAscending",
  LastUpdatedDescending = "lastUpdatedDescending",
  TemplateNameAscending = "templateNameAscending",
  TemplateNameDescending = "templateNameDescending",
  TotalGapCountAscending = "totalGapCountAscending",
  TotalGapCountDescending = "totalGapCountDescending",
  Unknown = "unknown",
}

export enum GroupByOption {
  LineOfBusiness = "lineOfBusiness",
  None = "none",
}

// Canonical labelled option list — single source of truth also consumed by SortGroupControls.tsx
// for its rendered <option> list, so a new option only needs to be added here.
export const SORT_OPTIONS: readonly { label: string; value: SortByOption }[] = [
  { label: "Total Gap Count Ascending", value: SortByOption.TotalGapCountAscending },
  { label: "Total Gap Count Descending", value: SortByOption.TotalGapCountDescending },
  { label: "Adequacy Score Ascending", value: SortByOption.AdequacyScoreAscending },
  { label: "Adequacy Score Descending", value: SortByOption.AdequacyScoreDescending },
  { label: "County Count Ascending", value: SortByOption.CountyCountAscending },
  { label: "County Count Descending", value: SortByOption.CountyCountDescending },
  { label: "Last Run On Ascending", value: SortByOption.LastUpdatedAscending },
  { label: "Last Run On Descending", value: SortByOption.LastUpdatedDescending },
  { label: "Archive Date Ascending", value: SortByOption.ArchiveDateAscending },
  { label: "Archive Date Descending", value: SortByOption.ArchiveDateDescending },
  { label: "Template (A → Z)", value: SortByOption.TemplateNameAscending },
  { label: "Template (Z → A)", value: SortByOption.TemplateNameDescending },
  { label: "Project Name (A → Z)", value: SortByOption.AlphabeticalAZ },
  { label: "Project Name (Z → A)", value: SortByOption.AlphabeticalZA },
];

// Every option a user can pick in any view — the recognized-value set sortGroupStorage.ts validates
// persisted state against. Excludes Unknown, which is a server-side default, never a selection.
export const SELECTABLE_SORT_OPTIONS: readonly SortByOption[] = SORT_OPTIONS.map((option) => option.value);

// Archive Date is offered only in the archived view.
const ARCHIVED_ONLY_SORT_OPTIONS: readonly SortByOption[] = [
  SortByOption.ArchiveDateAscending,
  SortByOption.ArchiveDateDescending,
];

// Offered in the Active and Hidden views, which share one option set (see sortGroupStorage.ts).
export const ACTIVE_SORT_OPTIONS: readonly SortByOption[] = SELECTABLE_SORT_OPTIONS
  .filter((value) => !ARCHIVED_ONLY_SORT_OPTIONS.includes(value));

// Narrower than Active/Hidden: Total Gap Count / Adequacy Score / County Count depend on run data
// that may be stale for old archived projects, so they stay out; Archive Date is offered here only.
export const ARCHIVED_VALID_SORT_OPTIONS: readonly SortByOption[] = [
  SortByOption.LastUpdatedAscending,
  SortByOption.LastUpdatedDescending,
  SortByOption.ArchiveDateAscending,
  SortByOption.ArchiveDateDescending,
  SortByOption.TemplateNameAscending,
  SortByOption.TemplateNameDescending,
  SortByOption.AlphabeticalAZ,
  SortByOption.AlphabeticalZA,
];

export interface SortGroupState {
  groupBy: GroupByOption;
  sortBy: SortByOption;
}

/**
 * The default Sort/Group selection, to fallback to.
 */
export function defaultSortGroupState(): SortGroupState {
  return {
    groupBy: GroupByOption.None,
    sortBy: SortByOption.AlphabeticalAZ,
  };
}

/**
 * A partition of the filtered project set for a single Group By value (e.g. one Line of Business),
 * returned by the project-resources/grouped endpoint. The frontend renders one collapsible section
 * per group and lazily paginates {@link projectIds} through the projects-data endpoint.
 */
export interface ProjectGroup {
  key: string;
  label: string;
  projectIds: number[];
}

export interface ProjectResourcesRequest {
  linesOfBusinessIds?: readonly number[];
  /** Role-restricted action-status filter (Quest User / Client Admin); omitted ⇒ "All". */
  projectAction?: NetworkPortfolioManagementProjectActionStatus;
  /** Project run-status filter; Publish Pending and Not Run are Preview-only and are stripped client-side for other users. When omitted, no status filtering is applied. */
  projectStatuses?: readonly NetworkPortfolioManagementProjectStatus[];
  /** Network-assessment project types; omitted ⇒ no project-type filtering. */
  projectTypes?: readonly NetworkPortfolioManagementProjectTypeEnum[];
  /** Role-restricted Project Utilization category ids (Quest User / Client Admin); omitted ⇒ no filtering. */
  projectUtilizationCategoryIds?: readonly number[];
  searchTerm?: string;
  serviceAreaCounties?: readonly { state: string; county: string }[];
  serviceAreaStates?: readonly string[];
  specialtyGroups?: readonly SpecialtyGroupRef[];
  tagIds?: readonly number[];
  templateNames?: readonly string[];
}

export interface ProjectsDataRequest {
  pageIndex: number;
  pageSize: number;
  projectIds: number[];
  sortBy: SortByOption;
}

export interface SortedProjectResourcesRequest {
  projectIds: number[];
  sortBy: SortByOption;
}

export interface GroupedProjectResourcesRequest {
  groupBy: GroupByOption;
  projectIds: number[];
  sortBy: SortByOption;
}

/**
 * Describes a single filter control in the NPM filter bar. The bar renders these in array order.
 * `pinned` filters always stay inline; the remaining (overflow-candidate) filters collapse into the
 * "More Filters" fly-out when the bar runs out of room. `isActive` reflects whether the
 * filter currently has a selection and drives the overflow button's count badge.
 */
export interface FilterDescriptor {
  /** Stable identifier; also used as the React key and the overflow-measurement key. */
  id: string;
  /** True when the filter currently has an active selection. */
  isActive: boolean;
  /** Pinned filters are always visible inline; non-pinned filters are overflow candidates. */
  pinned: boolean;
  render: () => ReactNode;
}
